import { useCallback, useMemo, useState } from 'react'
import { useLazyQuery, useQuery } from '@apollo/client/react'
import {
  OrgTicketTimelineDocument,
  type OrgTicketTimelineQuery,
} from '@/graphql/generated/graphql'

export type OrgTicketTimelineItem =
  OrgTicketTimelineQuery['orgTicketTimeline']['items'][number]

/**
 * Tamaño de página. El servidor recorta a 50 y su defecto es 30 (goal
 * portal-correcciones-4); se pide explícito para que la vista no dependa del
 * defecto del backend.
 */
export const TIMELINE_PER_PAGE = 30

interface OlderPages {
  code: string
  pages: OrgTicketTimelineItem[][]
}

/**
 * Cronología del ticket para pintarla como línea de tiempo.
 *
 * El servidor entrega la página 1 = la MÁS RECIENTE, ordenada descendente
 * (decisión 4 del goal portal-correcciones-4). Aquí se acumulan las páginas en
 * ese mismo orden y se invierte UNA sola vez al final, de modo que `items` sale
 * **ascendente**: la historia se lee de arriba abajo.
 *
 * Un evento nunca sale dos veces: al fusionar se deduplica por `id` (dos
 * páginas consecutivas pueden solaparse si llega actividad nueva entre una
 * petición y la siguiente).
 */
export function useOrgTicketTimeline(code: string) {
  const { data, loading, error, refetch } = useQuery(OrgTicketTimelineDocument, {
    variables: { code, page: 1, perPage: TIMELINE_PER_PAGE },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })
  // Las páginas anteriores se piden a demanda y NO se cachean como la 1: son
  // historia inmutable que solo interesa mientras la pantalla está abierta.
  const [fetchOlder, { loading: loadingOlder }] = useLazyQuery(
    OrgTicketTimelineDocument,
    { fetchPolicy: 'network-only' },
  )
  const [older, setOlder] = useState<OlderPages>({ code, pages: [] })

  // Cambiar de ticket sin desmontar la página descarta lo acumulado del
  // anterior. Se deriva en render: nada de efecto que resincronice estado.
  // Memorizado para que el `[]` del caso «otro ticket» no cambie de identidad
  // en cada render y reviente los `useMemo` de abajo.
  const loadedPages = useMemo(
    () => (older.code === code ? older.pages : []),
    [older, code],
  )

  const page = data?.orgTicketTimeline
  const total = page?.total ?? 0

  const descending = useMemo(() => {
    const seen = new Set<string>()
    const merged: OrgTicketTimelineItem[] = []
    for (const item of [...(page?.items ?? []), ...loadedPages.flat()]) {
      if (seen.has(item.id)) continue
      seen.add(item.id)
      merged.push(item)
    }
    return merged
  }, [page?.items, loadedPages])

  const items = useMemo(() => [...descending].reverse(), [descending])

  const loadOlder = useCallback(async () => {
    // La página 1 la sirve el useQuery; lo acumulado empieza en la 2.
    const nextPage = loadedPages.length + 2
    const res = await fetchOlder({
      variables: { code, page: nextPage, perPage: TIMELINE_PER_PAGE },
    })
    const fetched = res.data?.orgTicketTimeline.items ?? []
    // Página vacía: no se avanza el cursor, así el botón reintenta la misma.
    if (fetched.length === 0) return
    setOlder((prev) =>
      prev.code === code
        ? { code, pages: [...prev.pages, fetched] }
        : { code, pages: [fetched] },
    )
  }, [code, fetchOlder, loadedPages.length])

  /**
   * Vuelve a la página 1 y descarta lo anterior. Es lo que se hace al comentar:
   * el mensaje nuevo es el final del riel, y mantener páginas viejas cargadas
   * con offsets ya corridos solo produce huecos.
   */
  const resetToLatest = useCallback(async () => {
    setOlder({ code, pages: [] })
    await refetch({ code, page: 1, perPage: TIMELINE_PER_PAGE })
  }, [code, refetch])

  return {
    /** Eventos en orden ASCENDENTE (el más antiguo primero). */
    items,
    total,
    hasMore: descending.length < total,
    /** Solo la primera carga: un refetch no debe vaciar el riel. */
    initialLoading: loading && !page,
    loadingOlder,
    error,
    loadOlder,
    resetToLatest,
    refetch,
  }
}
